import * as React from 'react';
import translate from '../../../../locale/translate';

import ISettings from './ISettings';

import { getDisplayValue, formatDate } from '../../util/StringUtil';
import HintList from './HintList';

interface IProps {
    node: any,
    states: any[],
    withState: boolean,
    settings: ISettings,
}

const AVAILABLE_COLS = [
    'name',
    'date',
    'lastDate',
    'resolved',
];

const COL_TITLES = {
    name: translate.gettext("Name"),
    date: translate.gettext("First reported"),
    lastDate: translate.gettext("Last reported"),
    resolved: translate.gettext("Outdated"),
};

const HEADER_CLASSES = {
    date: 'col-xs-2 text-right',
    lastDate: 'col-xs-2 text-right',
    resolved: 'text-right',
};

const COL_CLASSES = {
    date: 'small text-right',
    lastDate: 'small text-right',
    resolved: 'text-right',
};

export default class StateTable extends React.Component<IProps, void> {
    static propTypes = {
        node: React.PropTypes.object.isRequired,
        states: React.PropTypes.array,
        withState: React.PropTypes.bool,
        settings: React.PropTypes.object.isRequired,
    };

    trimMessage(message: string) {
        if (message.length < 160) {
            return message;
        }

        return `${message.substr(0, 160)}...`;
    }

    renderResolved(resolved: boolean) {
        return resolved ? (<i className="fa fa-check text-success" />) : (<i className="fa fa-times text-danger" />);
    }

    renderColumnHead(name: string, settings: ISettings) {
        if (getDisplayValue(name, settings)) {
            return (
                <th key={name} className={HEADER_CLASSES[name]}>{COL_TITLES[name]}</th>
            );
        }

        return;
    }

    renderColumn(name: string, state, settings: ISettings) {
        if (getDisplayValue(name, settings)) {
            let value;

            switch (name) {
                case 'date': value = formatDate(state.date, settings.dateFormat); break;
                case 'lastDate': value = formatDate(state.lastDate, settings.dateFormat); break;
                case 'resolved': value = this.renderResolved(state.resolved); break;
            }

            return (
                <td key={name} className={COL_CLASSES[name]}>{value}</td>
            );
        }

        return;
    }

    renderHintRow(state, colCount: number, settings: ISettings) {
        const hints = (state.hints || []).filter(hint => !hint.private);
        const key = `hints-${state.sId}`;

        if (settings.displayHints && hints.length) {
            return (
                <tr key={key}>
                    <td></td>
                    <td colSpan={colCount}>
                        <HintList hints={hints} />
                    </td>
                </tr>
            );
        }
    }

    renderRow(state, idx: number, settings: ISettings) {
        return (
            <tr key={state.sId}>
                <td className="small muted">{idx + 1}</td>
                <td>{this.trimMessage(state.message)}</td>
                {AVAILABLE_COLS.map(name => {
                    return this.renderColumn(name, state, settings);
                })}
            </tr>
        );
    }

    render() {
        let { states } = this.props;
        const { node, withState, settings } = this.props;

        const key = `states-${node.id}`;
        const headers = AVAILABLE_COLS.map(name => {
            return this.renderColumnHead(name, settings);
        }).filter(header => !!header);
        const colCount = headers.length + 1;
        const nameColClasses = [];

        let rows;

        if (withState != null) {
            states = (states || []).filter(state => state.state === withState);
        }

        if (!states.length) {
            let key = `no-states-${node.id}`;
            rows = [
                <tr key={key}>
                    <td></td>
                    <td className="muted" colSpan={colCount}>{translate.gettext("No alerts occured")}</td>
                </tr>
            ];

        } else {
            rows = [];
            states.forEach((state, idx) => {
                rows.push(this.renderRow(state, idx, settings));

                if (settings.displayHints) {
                    const hintRow = this.renderHintRow(state, colCount, settings);
                    if (hintRow) {
                        rows.push(hintRow);
                    }
                }
            });
        }

        if (headers.length) {
            nameColClasses.push('col-xs-8');
        }

        return (
            <table key={key} className="table">
                <thead>
                    <tr>
                        <th style={{ width: '14px' }}></th>
                        <th className={nameColClasses.join(' ')}>{node.name}</th>
                        {headers}
                    </tr>
                </thead>
                <tbody>
                    {rows}
                </tbody>
            </table>
        );
    }
}


// WEBPACK FOOTER //
// ./src/components/widget/alerts/listAlerts/StateTable.tsx